/**
 * Growth-vs-Inefficiency Causal Decomposition (Patent Sub-system N)
 * ==================================================================
 * The CFO's #1 question answered: "Did customer growth cause the cost
 * spike, or did engineering break something?"
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  TrendingUp, Activity, Loader2, FlaskConical, Scale,
  ArrowUpRight, AlertTriangle,
} from 'lucide-react';
import { toast } from 'sonner';
import DemoDataBanner from '../components/DemoDataBanner';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtUsd = (n) => {
  if (n == null) return '$0';
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1_000_000) return `${sign}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${sign}$${(abs / 1_000).toFixed(1)}K`;
  return `${sign}$${abs.toFixed(0)}`;
};

const CostCausality = () => {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/cost-causality/demo`)
      .then((r) => setResult(r.data))
      .catch(() => toast.error('Could not load demo'))
      .finally(() => setLoading(false));
  }, []);

  // Build stacked-bar data
  const maxBarValue = result
    ? Math.max(...result.daily.map((d) => Math.abs(d.growth_term_usd) + Math.abs(d.waste_term_usd)))
    : 1;
  const totalDelta = result ? (result.growth_term_total_usd + result.waste_term_total_usd) : 0;
  const growthPct = result && totalDelta ? (result.growth_term_total_usd / totalDelta) * 100 : 0;
  const wastePct = result && totalDelta ? (result.waste_term_total_usd / totalDelta) * 100 : 0;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100" data-testid="cost-causality-page">
      <Helmet>
        <title>Cost Causality — Growth vs Inefficiency Decomposition | CARTIEAI</title>
        <meta name="description" content="Did customer growth cause the cloud spike, or did engineering break something? CARTIEAI mathematically decomposes every bill increase into Growth and Inefficiency terms. Patent-pending sub-system N." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <DemoDataBanner />
        {/* HERO */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-amber-300 border border-amber-500/30 rounded-md px-2 py-1 mb-5" data-testid="cc-patent-badge">
            <FlaskConical className="w-3 h-3" />
            Proprietary engine
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight leading-[1.05]"><BrandTitle>Cost Causality</BrandTitle></h1>
          <p className="mt-4 text-base sm:text-lg text-slate-300 leading-relaxed">
            The CFO&apos;s #1 question, finally answered: <span className="text-white font-semibold">Did customer growth do this, or did engineering break something?</span>
          </p>
          <p className="mt-3 text-sm text-slate-500">
            Every other FinOps tool shows raw cost deltas. None separate growth from waste mathematically. We decompose every bill spike into a <span className="text-emerald-300">growth term</span> (cost increase explainable by demand) and an <span className="text-amber-300">inefficiency term</span> (the residual = real engineering waste) — with 95% confidence intervals from bootstrap resampling.
          </p>
        </div>

        {loading && <Loader2 className="w-8 h-8 animate-spin mx-auto my-16 text-slate-500" />}

        {/* HEADLINE CARD */}
        {result && (
          <Card className="mt-10 bg-gradient-to-br from-amber-500/10 to-emerald-500/5 border-amber-500/30">
            <CardContent className="p-6 sm:p-8">
              <div className="text-xs uppercase tracking-widest text-amber-300 font-semibold mb-2">CFO summary</div>
              <p className="text-xl sm:text-2xl text-white font-semibold leading-relaxed" data-testid="cc-headline">{result.headline_summary}</p>
            </CardContent>
          </Card>
        )}

        {/* DECOMPOSITION */}
        {result && (
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="cc-decomposition">
            <Card className="bg-slate-900/60 border border-white/10 shadow-sm border-emerald-500/30">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-emerald-300 mb-2">
                  <ArrowUpRight className="w-4 h-4" />
                  <span className="text-[10px] uppercase tracking-widest font-semibold">Growth term</span>
                </div>
                <div className="text-3xl font-bold text-white" data-testid="cc-growth">{fmtUsd(result.growth_term_total_usd)}</div>
                <div className="text-xs text-slate-300 mt-1">{growthPct.toFixed(0)}% of total increase</div>
                <div className="text-[10px] text-slate-500 mt-2 font-mono">
                  95% CI: {fmtUsd(result.growth_term_ci_low)} – {fmtUsd(result.growth_term_ci_high)}
                </div>
                <div className="text-[11px] text-emerald-300 mt-3 leading-relaxed">
                  Cost increase explained by {result.demand_label} growth at baseline efficiency. <span className="text-emerald-300 font-semibold">This is healthy growth.</span>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/60 border border-white/10 shadow-sm border-amber-500/30">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-amber-300 mb-2">
                  <AlertTriangle className="w-4 h-4" />
                  <span className="text-[10px] uppercase tracking-widest font-semibold">Inefficiency term</span>
                </div>
                <div className="text-3xl font-bold text-white" data-testid="cc-waste">{fmtUsd(result.waste_term_total_usd)}</div>
                <div className="text-xs text-slate-300 mt-1">{wastePct.toFixed(0)}% of total increase</div>
                <div className="text-[10px] text-slate-500 mt-2 font-mono">
                  95% CI: {fmtUsd(result.waste_term_ci_low)} – {fmtUsd(result.waste_term_ci_high)}
                </div>
                <div className="text-[11px] text-amber-200 mt-3 leading-relaxed">
                  Residual cost increase NOT explained by demand. <span className="text-amber-400 font-semibold">This is engineering waste.</span>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/60 border border-white/10 shadow-sm border-white/10">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 text-slate-300 mb-2">
                  <Scale className="w-4 h-4" />
                  <span className="text-[10px] uppercase tracking-widest font-semibold">Baseline efficiency</span>
                </div>
                <div className="text-3xl font-bold text-white">${result.baseline_unit_cost.toFixed(2)}</div>
                <div className="text-xs text-slate-300 mt-1">per {result.demand_label} unit · per day</div>
                <div className="text-[10px] text-slate-500 mt-2 font-mono">Median of last 14-day stable window</div>
                <div className="text-[11px] text-slate-300 mt-3 leading-relaxed">If per-unit efficiency hadn&apos;t changed, this is what each unit of demand would cost.</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* DAILY DECOMPOSITION CHART */}
        {result && (
          <Card className="mt-8 bg-slate-900/60 border border-white/10 shadow-sm border-white/10">
            <CardHeader>
              <CardTitle className="text-white text-lg flex items-center gap-2">
                <Activity className="w-5 h-5 text-amber-300" /> Daily decomposition · last {result.daily.length} days
              </CardTitle>
              <CardDescription>Each bar shows the daily cost increase split into growth (green) vs waste (amber).</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2" data-testid="cc-daily-chart">
                {result.daily.map((d) => {
                  const growthW = Math.max(0, d.growth_term_usd);
                  const wasteW = Math.max(0, d.waste_term_usd);
                  const totalW = growthW + wasteW || 1;
                  const barFillPct = (totalW / maxBarValue) * 100;
                  return (
                    <div key={d.ts} className="flex items-center gap-3 text-xs">
                      <span className="text-slate-500 font-mono w-20 flex-shrink-0">{d.ts.slice(5)}</span>
                      <div className="flex-1 h-5 bg-slate-950 rounded overflow-hidden flex">
                        <div className="h-full bg-emerald-400/80" style={{ width: `${(growthW / totalW) * barFillPct}%` }} />
                        <div className="h-full bg-amber-400" style={{ width: `${(wasteW / totalW) * barFillPct}%` }} />
                      </div>
                      <span className="text-emerald-300 font-mono w-16 text-right">{fmtUsd(d.growth_term_usd)}</span>
                      <span className="text-amber-300 font-mono w-16 text-right">{fmtUsd(d.waste_term_usd)}</span>
                      <span className="text-white font-mono w-20 text-right font-semibold">{fmtUsd(d.actual_cost_usd)}</span>
                    </div>
                  );
                })}
                <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest text-slate-500 mt-3 pt-2 border-t border-white/5">
                  <span className="w-20 flex-shrink-0">date</span>
                  <span className="flex-1"></span>
                  <span className="w-16 text-right text-emerald-300">growth</span>
                  <span className="w-16 text-right text-amber-300">waste</span>
                  <span className="w-20 text-right">actual cost</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* METHOD */}
        <Card className="mt-16 bg-slate-900/60 border border-white/10 shadow-sm border-white/10">
          <CardContent className="p-6 sm:p-8">
            <div className="text-xs uppercase tracking-widest text-amber-300 font-semibold mb-3">The method</div>
            <h3 className="text-xl font-bold text-white mb-4">A structural decomposition, not a regression.</h3>
            <ol className="text-sm text-slate-200 space-y-3 leading-relaxed list-decimal list-inside">
              <li><strong className="text-white">Baseline unit-cost</strong> = median(cost / demand) over the configured stable window (default: 14 days preceding analysis).</li>
              <li><strong className="text-white">Growth term</strong> = baseline_unit_cost × (current_demand − baseline_demand). The cost the bill SHOULD increase by, given organic demand growth.</li>
              <li><strong className="text-white">Waste term</strong> = (current_cost − baseline_cost) − growth_term. The residual = real engineering waste.</li>
              <li><strong className="text-white">Confidence intervals</strong> via 500-sample bootstrap resampling of the baseline window — accounts for noise in the unit-cost estimate.</li>
            </ol>
            <p className="text-xs text-slate-500 mt-5">This is causal because it answers the counterfactual "what would the cost be if per-unit efficiency hadn&apos;t changed?" directly — not a correlation between two time-series.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default CostCausality;
